import { Component, Input, OnChanges, NgZone, OnInit, SimpleChanges } from '@angular/core';
import { VeranstaltungenService } from 'src/app/service/veranstaltungen.service';
import { IVeranstaltung } from 'src/app/models/IVeranstaltung.interface';
import { Anmeldung } from 'src/app/models/IAnmeldung.interface';
import { ToastrService } from 'ngx-toastr';
import { FilterEngineService } from 'src/app/service/filter-engine.service';
import { ExportService } from 'src/app/service/export-service.service';
import { IActionButton } from 'src/app/utils/filter/buttons';
import { getTeilnehmerSearchCategories, ITeilnehmer } from 'src/app/models/ITeilnehmer.interface';
import { IStamm } from 'src/app/models/IStamm.interface';
import { DetailsTnComponent } from 'src/app/teilnehmer/details-tn/details-tn.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {Icon} from "../../../utils/icon/icon.component";
import {PdfGeneratorService} from "../../../service/pdf-generator.service";
import {StammNamePipe} from "../../../filters/stamm-name.pipe";
import {TeilnehmerlisteTitelComponent} from "../../../utils/teilnehmerliste-titel/teilnehmerliste-titel.component";

type AnmeldungMitEdit = Anmeldung & {edit?: boolean}

@Component({
  selector: 'app-tn-list',
  templateUrl: './tn-list.component.html',
  styleUrls: ['./tn-list.component.scss']
})
export class TnListComponent implements OnChanges, OnInit {

  @Input()
  public eventId: number;
  public event: IVeranstaltung;
  public loading: boolean;
  public readonly Icon = Icon;
  public anmeldungen: Map<IStamm, AnmeldungMitEdit[]> = new Map();
  public sortetStaemme: IStamm[] = [];
  public useUrlBarInSearch = false;
  public categories: any;
  public query = "";
  public expandedStaemme: {[stammId: number]: boolean} = {};
  private fullList: AnmeldungMitEdit[] = [];
  private initialized = false;
  private shouldAutoExpandSingleStamm = false;
  actionButtons: IActionButton[] = [
    { title: 'Export', value: '<i class="fas fa-file-export"></i>', callback: () => this.export(), mobile:false },
    { title: 'Aktualisieren', value: '<i class="fas fa-sync-alt"></i>', callback: () => { this.refreshList(); }, mobile:true },
    { title: 'Preise zurücksetzen', value: '<i class="fas fa-euro-sign"></i>', callback: () => this.resetPrices(), mobile:true },
    { title: 'Teilnehmerliste', value: '<i class="fas fa-clipboard-list"></i>', callback: () => this.printTeilnehmerliste(), mobile:true, position: 'right'}
  ];


  constructor(
    private eventService: VeranstaltungenService,
    private filter: FilterEngineService,
    private zone: NgZone,
    private toastr: ToastrService,
    private exportService: ExportService,
    private ngbModal: NgbModal,
    private pdfService: PdfGeneratorService) {

    this.categories = getTeilnehmerSearchCategories();
    this.loading = true;
  }

  public ngOnInit(): void {
    this.initialized = true;
    this.loadEvent();
    if (this.event) {
      this.refreshList();
    }
  }

  public async refreshList(): Promise<void> {
    if (!this.event) {
      return;
    }

    try {
      this.loading = true;
      this.shouldAutoExpandSingleStamm = true;
      this.fullList = (await this.eventService.getPricesToEvent(this.event))
      .filter(a => !!a.teilnehmer)
      .sort((a1,a2) => {
          const d = a1.teilnehmer.nachname.localeCompare(a2.teilnehmer.nachname);
          if(d === 0){
            return a1.teilnehmer.vorname.localeCompare(a2.teilnehmer.vorname);
          }
          return d;
      });
      await this.onFilterChange(this.query);
    } catch (error) {
      console.log('Failed to load Teilnehmerliste: ', error);
    } finally {
      this.loading = false;
    }
  }

  public async savePrices() {
    console.log('Preise Speichern');
    try {
      this.loading = true;
      const msg = await this.eventService.setPricesToEvent(this.fullList);
      this.toastr.success(msg, 'Erfolgreich');
    }
    catch (msg) {
      this.toastr.error(msg, 'Fehler');
    }
    this.anmeldungen = new Map();
    this.fullList = [];
    await this.refreshList();
  }

  public async onFilterChange(newQuery: string) {
    if (typeof newQuery !== "string") {
      return;
    }
    this.query = newQuery;
    try {
      const tnList = this.fullList.map(a => a.teilnehmer);
      const anmeldungByTn = new Map<ITeilnehmer, AnmeldungMitEdit>();
      this.fullList.forEach(anmeldung => anmeldungByTn.set(anmeldung.teilnehmer, anmeldung));
      const filteredList = await this.filter.filter(newQuery, tnList);
      this.zone.run(() => {
        const filteredAnmeldungen = filteredList
          .map(tn => anmeldungByTn.get(tn))
          .filter((anmeldung): anmeldung is AnmeldungMitEdit => !!anmeldung);
        this.anmeldungen = this.groupAnmeldungen(filteredAnmeldungen);
        this.sortetStaemme = Array.from(this.anmeldungen.keys()).sort((s1,s2) => {
          return s1.name.localeCompare(s2.name);
        });
        this.expandSingleStammOnLoad();
      });
    } catch (error) {
      console.log('Failed to filter: ', error);
    }
  }

  public setSort(newSortKey: string) {
    const elements = this.query.split(" ");
    const withoutSearch = elements.filter(e => !e.startsWith("$"));
    this.query = withoutSearch.join(" ") + " " + newSortKey;
  }

  private groupAnmeldungen(anmeldungen: AnmeldungMitEdit[]): Map<IStamm, AnmeldungMitEdit[]> {
    const map: Map<IStamm, AnmeldungMitEdit[]> = new Map();
    anmeldungen.forEach(a => {
      if(!map.get(a.teilnehmer.stamm)){
        map.set(a.teilnehmer.stamm, []);
      }
      map.get(a.teilnehmer.stamm).push(a);
    });
    return map;
  }

  public toggleStamm(stamm: IStamm): void {
    this.expandedStaemme[stamm.id] = !this.expandedStaemme[stamm.id];
  }

  public isStammExpanded(stamm: IStamm): boolean {
    return !!this.expandedStaemme[stamm.id];
  }

  private expandSingleStammOnLoad(): void {
    if (!this.shouldAutoExpandSingleStamm) {
      return;
    }
    this.shouldAutoExpandSingleStamm = false;
    if (this.sortetStaemme.length === 1) {
      this.expandedStaemme[this.sortetStaemme[0].id] = true;
    }
  }

  public trackByStammId(index: number, stamm: IStamm): number {
    return stamm.id;
  }

  public trackByAnmeldungId(index: number, anmeldung: AnmeldungMitEdit): number {
    return anmeldung.teilnehmer.id;
  }

  private loadEvent(): void {
    this.event = this.eventService.getEvent(this.eventId);
  }

  public ngOnChanges(changes?: SimpleChanges): void {
    this.loadEvent();
    if (!this.initialized) {
      return;
    }
    if (!changes || changes.eventId) {
      this.refreshList();
    }
  }

  public openDetails(tn: ITeilnehmer): void{
    const activeModal = this.ngbModal.open(DetailsTnComponent, {
      size: 'lg',
      windowClass: 'w-100'
    });
    activeModal.componentInstance.tnId = tn.id;
  }

  private export() {
    const visibleTns = [];
    this.anmeldungen.forEach(subList => visibleTns.push(...this.toExportRows(subList)));
    this.exportService.export(visibleTns, "Angemeldete Teilnehmer - " + this.event.titel);
  }

  public exportStamm(stamm: IStamm) {
    const subList = this.anmeldungen.get(stamm) || [];
    this.exportService.export(
      this.toExportRows(subList),
      "Angemeldete Teilnehmer - " + this.event.titel + " - " + new StammNamePipe().transform(stamm)
    );
  }

  private toExportRows(subList: AnmeldungMitEdit[]): any[] {
    return subList.map(an => {
      const anExport = {};
      Object.assign(anExport, {
        preis: an.preis,
        isMitarbeiter: an.mitarbeiter,
        isAusgefallen: an.ausgefallen
      }, an.teilnehmer);
      return anExport;
    });
  }

  private resetPrices() {
    this.fullList.forEach(a => {
      let newPrice = a.mitarbeiter ? this.event.mitarbeiter_preis : this.event.preis;
      newPrice = a.ausgefallen ? this.event.ausfallgebuehr : newPrice;
      if(a.preis !== newPrice) {
        a.preis = newPrice;
        a.edit = true;
      }
    });
  }

  private printTeilnehmerliste(){
    this.askTitel().then(titel => {
      if (titel === null) {
        return;
      }
      const alle = Array.from(this.anmeldungen.values()).reduce((acc, val) => acc.concat(val), []);
      const pdf = this.pdfService.generateTeilnehmerliste(this.event, alle, titel);
      pdf.download(`Teilnehmerliste_${this.event.id}.pdf`);
    });
  }

  public printTeilnehmerlisteStamm(stamm: IStamm){
    this.askTitel().then(titel => {
      if (titel === null) {
        return;
      }
      const subList = this.anmeldungen.get(stamm) || [];
      const stammName = new StammNamePipe().transform(stamm);
      const pdf = this.pdfService.generateTeilnehmerliste(this.event, subList, titel, stammName);
      pdf.download(`Teilnehmerliste_${this.event.id}_${this.cleanFilename(stammName)}.pdf`);
    });
  }

  private askTitel(): Promise<string | null> {
    const modal = this.ngbModal.open(TeilnehmerlisteTitelComponent, { size: 'lg' });
    return modal.result.then(
      (titel: string) => titel,
      () => null
    );
  }

  private cleanFilename(raw: string): string {
    return raw.replace(/[^-a-zA-Z0-9_.()äöüß ]/g, '_');
  }

  public existing(tns: ITeilnehmer[]): number {
    return tns.filter(tn => tn && tn.vorname !== "deleted").length;
  }
}
